import { Field } from 'react-final-form';
import MediaUploadField from '../containers/DreamHomePage/DreamHomePageForm/MediaUploadField';
import { allOptions } from './general';
import { MoodboardLabel, PlanningPermissionLabel, ReviewFields, StringBoolean } from './enums';
import moment from 'moment';

// Utility to format dates
const formatDate = date => moment(date).format('LL');
const makeMediaHandler = (fieldName, labelId, defaultMsg) => ({
  label,
  values,
  formatMessage,
}) => ({
  key: label,
  value: (
    <Field
      disabled
      component={MediaUploadField}
      name={fieldName}
      values={values}
      label={formatMessage({ id: labelId, defaultMessage: defaultMsg })}
      isFinalReviewTab
    />
  ),
});
// Handlers for different field types
const fieldValueHandlers = {
  planningPermissionMedia: makeMediaHandler(
    ReviewFields.PLANNING_PERMISSION_MEDIA,
    PlanningPermissionLabel.ID,
    PlanningPermissionLabel.DEFAULT
  ),
  moodboardMediaFiles: makeMediaHandler(
    ReviewFields.MOODBOARD_MEDIA_FILES,
    MoodboardLabel.ID,
    MoodboardLabel.DEFAULT
  ),

  plotAddress: ({ fieldValue, label }) =>
    fieldValue?.selectedPlace?.address
      ? { key: label, value: fieldValue.selectedPlace.address }
      : null,

  date: ({ fieldValue, label }) =>
    fieldValue?.date ? { key: label, value: formatDate(fieldValue.date) } : null,

  array: ({ fieldValue, label }) => ({
    key: label,
    value: (
      <ul>
        {fieldValue.map((option, idx) => (
          <li key={idx}>{allOptions[option] || option}</li>
        ))}
      </ul>
    ),
  }),

  option: ({ fieldValue, label }) =>
    allOptions[fieldValue] ? { key: label, value: allOptions[fieldValue] } : null,

  string: ({ fieldValue, label }) => ({ key: label, value: fieldValue }),
};

// Main function to process review details
export const fetchReviewDetail = (fields, values, formatMessage) =>
  fields
    .map(({ name, label }) => {
      const fieldValue = values[name];
      if (!fieldValue) return null;

      const handlers = [
        {
          condition: name === ReviewFields.PLANNING_PERMISSION_MEDIA,
          handler: fieldValueHandlers.planningPermissionMedia,
        },
        {
          condition: name === ReviewFields.MOODBOARD_MEDIA_FILES,
          handler: fieldValueHandlers.moodboardMediaFiles,
        },
        {
          condition: name === ReviewFields.PLOT_ADDRESS && typeof fieldValue === 'object',
          handler: fieldValueHandlers.plotAddress,
        },
        {
          condition: typeof fieldValue === 'object' && fieldValue?.date,
          handler: fieldValueHandlers.date,
        },
        {
          condition: Array.isArray(fieldValue),
          handler: fieldValueHandlers.array,
        },
        {
          condition: typeof fieldValue === 'string' && allOptions[fieldValue],
          handler: fieldValueHandlers.option,
        },
        {
          condition: typeof fieldValue === 'string',
          handler: fieldValueHandlers.string,
        },
      ];

      const matchedHandler = handlers.find(({ condition }) => condition);
      return matchedHandler?.handler({ fieldValue, label, values, formatMessage }) ?? null;
    })
    .filter(Boolean);

/**
 * Retrieves and parses the 'dreamHomePage' item from sessionStorage.
 * Returns an empty object if the window is undefined, if the data is missing,
 * or if any error occurs during parsing.
 *
 * @returns {Object} The parsed data from sessionStorage or an empty object.
 */
export const getDreamHomePageData = () => {
  // Check if window is defined (e.g., not during SSR)
  if (typeof window === 'undefined') {
    return {};
  }

  try {
    const data = sessionStorage.getItem('dreamHomePage');
    return data ? JSON.parse(data) : {};
  } catch (error) {
    console.error('Error retrieving or parsing dreamHomePage from sessionStorage:', error);
    return {};
  }
};

export const getIsFirstStepCompleted = () => {
  // Not available during SSR
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    const flag = sessionStorage.getItem('isFirstStepCompleted');
    return flag === StringBoolean.TRUE;
  } catch (error) {
    console.error('Error retrieving or parsing isFirstStepCompleted from sessionStorage:', error);
    return false;
  }
};
export const getIsPropertyDeveloperPage = () => {
  // Not available during SSR
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    const flag = sessionStorage.getItem('isPropertyDeveloperPage');
    return flag === StringBoolean.TRUE;
  } catch (error) {
    console.error(
      'Error retrieving or parsing isPropertyDeveloperPage from sessionStorage:',
      error
    );
    return false;
  }
};

export const dreamHomagePageNotificationData = completeListingLink => {
  return {
    subject: 'Listing Completion Update for Admin Review',
    forAdmin: true,
    html: `<div
      style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; border: 1px solid #e0e0e0; border-radius: 8px; background-color: #f9f9f9;"
    >
      <h3 style="color: #333; font-size: 24px; margin-bottom: 20px;">Hello</h3>
      <p style="color: #555; font-size: 16px; line-height: 1.5; margin-bottom: 15px;">
        Please click the link below to view the completed listing:
        <a
          style="color: #1a73e8; text-decoration: none; font-weight: bold; padding: 2px 4px; border-radius: 4px; transition: background-color 0.2s;"
          href="${completeListingLink}"
          target="_blank"
          onmouseover="this.style.backgroundColor='#e8f0fe'"
          onmouseout="this.style.backgroundColor='transparent'"
        >
          View Listing
        </a>
      </p>
      <p style="color: #555; font-size: 16px; line-height: 1.5; margin-bottom: 15px;">
        If the above link is not clickable, you can copy and paste the URL below into your browser:
      </p>
      <p
        style="color: #555; font-size: 16px; line-height: 1.5; margin-bottom: 15px; word-break: break-all;"
      >
        <code style="background-color: #e0e0e0; padding: 2px 6px; border-radius: 4px;"
          >${completeListingLink}</code
        >
      </p>
      <p style="color: #555; font-size: 16px; line-height: 1.5; margin-bottom: 0;">
        Thank you for your prompt attention to this matter.
      </p>
    </div>`,
  };
};
